/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Action, ActionCreators } from './actions';
import Terminal, {
  FlightPathWaypoint,
  InstallationType,
  MAX_AZIMUTH,
  MAX_ELEVATION,
  MAX_HEADING,
  MAX_LATITUDE,
  MAX_LONGITUDE,
  MAX_PING_LOSS_PERCENTAGE,
  MAX_PITCH,
  MAX_ROLL,
  MIN_AZIMUTH,
  MIN_ELEVATION,
  MIN_HEADING,
  MIN_LATITUDE,
  MIN_LONGITUDE,
  MIN_PITCH,
  MIN_ROLL,
  SatelliteInFov,
  TerminalDetails,
  ZERO,
} from '../../shared/entities/terminal';
import reject from 'lodash/reject';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import { getHighestPriorityConnectivityStatus, isOnline } from '../../utils/ConnectionStatusUtils';
import { getOtherTerminalOnAircraft } from '../../utils/TerminalUtilities';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import { isPlatformTypeInList } from '../../utils/PlatformTypeUtils';
import unset from 'lodash/unset';
import { PortalsEnum } from '../../shared/entities/portal';

export interface State {
  readonly isRequestingTerminals: boolean;
  readonly isRequestingTailDetailsPanelTerminal: boolean;
  readonly isInitialTerminalsRequestFinished: boolean;
  readonly isRequestingFlightPath: boolean;
  readonly terminals: { [key: string]: Terminal };
  readonly terminalsByTerminalUuid: { [key: string]: Terminal };
  readonly requestTerminalsError?: string;
  readonly tailDetailsPanelTerminal: TerminalDetails | undefined;
  readonly selectedTerminalFlightPath: { [key: string]: FlightPathWaypoint[] | undefined };
  readonly requestTailDetailsPanelTerminalError?: string;
  readonly requestingFlightPathError?: { [key: string]: string | undefined };
}

export const initialTerminalsState: State = {
  terminals: {},
  terminalsByTerminalUuid: {},
  isRequestingTerminals: false,
  isInitialTerminalsRequestFinished: false,
  requestTerminalsError: undefined,

  // Tail Details Panel Terminal
  tailDetailsPanelTerminal: undefined,
  isRequestingTailDetailsPanelTerminal: false,
  requestTailDetailsPanelTerminalError: undefined,

  // Flight path for selected terminal
  selectedTerminalFlightPath: {
    [PortalsEnum.UNIFIED_PORTAL]: undefined,
    [PortalsEnum.ISAN_PORTAL]: undefined,
  },
  isRequestingFlightPath: false,
  requestingFlightPathError: {
    [PortalsEnum.UNIFIED_PORTAL]: undefined,
    [PortalsEnum.ISAN_PORTAL]: undefined,
  },
};

// Reducer
export default function reducer(state: State = initialTerminalsState, action: Action): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.RequestTerminals.type:
      partialState = {
        requestTerminalsError: undefined,
        isRequestingTerminals: true,
        isRequestingTailDetailsPanelTerminal: false,
      };
      break;

    case ActionCreators.ReceivedTerminalsSuccess.type: {
      const compiledTerminals = {};
      const terminalsByTerminalUuid = {};

      action.payload.response.forEach((terminal: Terminal) => {
        terminal = massageTerminal(terminal);
        terminal = massageTerminalDisplayName(terminal, action.payload.response);
        if (terminal.datapathUuid) {
          compiledTerminals[terminal.datapathUuid] = terminal;
        }
        if (terminal.terminalUuid) {
          terminalsByTerminalUuid[terminal.terminalUuid] = terminal;
        }
      });

      partialState = {
        terminals: compiledTerminals,
        terminalsByTerminalUuid,
        requestTerminalsError: undefined,
        isRequestingTerminals: false,
        isInitialTerminalsRequestFinished: true,
      };
      break;
    }
    case ActionCreators.ReceivedTerminalUpdateSuccess.type: {
      const currentTerminals = { ...state.terminals };

      if (action.payload && action.payload.length > 0) {
        action.payload.forEach((updatedTerminal: Terminal) => {
          updatedTerminal = massageTerminal(updatedTerminal);
          updatedTerminal = massageTerminalDisplayName(updatedTerminal, action.payload);
          currentTerminals[updatedTerminal.datapathUuid] = updatedTerminal;
        });
      }

      partialState = {
        terminals: currentTerminals,
        requestTerminalsError: undefined,
        isRequestingTerminals: false,
        isInitialTerminalsRequestFinished: true,
      };
      break;
    }
    case ActionCreators.ReceivedTerminalsError.type:
      partialState = {
        requestTerminalsError: action.payload.message,
        isRequestingTerminals: false,
        isInitialTerminalsRequestFinished: true,
      };
      break;

    case ActionCreators.RequestTailDetailsPanelTerminal.type:
      partialState = {
        requestTailDetailsPanelTerminalError: undefined,
        isRequestingTailDetailsPanelTerminal: true,
      };
      break;

    case ActionCreators.ReceivedTerminalSuccess.type:
      let terminal: TerminalDetails = { ...action.payload.terminal };
      let useOtherTerminalOnPlatformDataForUpdate = action.payload.useOtherTerminalOnPlatformDataForUpdate;
      let flightPath = state.selectedTerminalFlightPath[action.payload.portal]
        ? (state.selectedTerminalFlightPath[action.payload.portal] as FlightPathWaypoint[]).slice()
        : state.selectedTerminalFlightPath[action.payload.portal];
      terminal = massageTerminalDetails(terminal);
      if (size(terminal.terminalsInBeam)) {
        terminal.terminalsInBeam = terminal.terminalsInBeam.map((terminal: Terminal) =>
          massageTerminalDisplayName(terminal, [], state.terminals),
        );
      }

      let terminalConnStatus = terminal.connectivityStatus;
      let otherTerminalConnStatus = getOtherTerminalOnAircraft(terminal).connectivityStatus;

      if (flightPath && flightPath.length > 0) {
        const addWaypoint = (waypoint: FlightPathWaypoint, flightPathCoords: FlightPathWaypoint[] | undefined) => {
          if (flightPathCoords && flightPathCoords.length > 0) {
            flightPathCoords = flightPathCoords.slice();
            let lastFlightPathLatLng = flightPathCoords[flightPathCoords.length - 1];

            // add terminal's latitude and longitude to the flight path if not already present
            if (
              lastFlightPathLatLng.sourceTime < waypoint.sourceTime &&
              (lastFlightPathLatLng.latitudeDeg !== waypoint.latitudeDeg ||
                lastFlightPathLatLng.longitudeDeg !== waypoint.longitudeDeg)
            ) {
              flightPathCoords.push(waypoint);
            }
          }

          return flightPathCoords;
        };

        const isKuPlusKaTerminal = terminal.installationType === InstallationType.KU_PLUS_KA;

        let waypointsToAdd = [] as FlightPathWaypoint[];

        // Add current terminal's waypoint
        if (
          (isKuPlusKaTerminal || !useOtherTerminalOnPlatformDataForUpdate) &&
          !isNil(terminal.latitudeDeg) &&
          !isNil(terminal.longitudeDeg) &&
          isOnline(terminal.connectivityStatus)
        ) {
          waypointsToAdd.push({
            latitudeDeg: terminal.latitudeDeg,
            longitudeDeg: terminal.longitudeDeg,
            sourceTime: terminal.sourceTime,
            platformConnectivityStatus: getHighestPriorityConnectivityStatus(
              terminalConnStatus,
              otherTerminalConnStatus,
            ),
          });
        }

        // Add other terminal on aircraft's waypoint
        if (
          (isKuPlusKaTerminal || useOtherTerminalOnPlatformDataForUpdate) &&
          terminal.terminalsOnAircraft &&
          terminal.terminalsOnAircraft.length &&
          !isNil(terminal.terminalsOnAircraft[0].latitudeDeg) &&
          !isNil(terminal.terminalsOnAircraft[0].longitudeDeg) &&
          isOnline(terminal.terminalsOnAircraft[0].connectivityStatus)
        ) {
          const otherTerm = terminal.terminalsOnAircraft[0];
          waypointsToAdd.push({
            latitudeDeg: otherTerm.latitudeDeg,
            longitudeDeg: otherTerm.longitudeDeg,
            sourceTime: otherTerm.sourceTime,
            platformConnectivityStatus: getHighestPriorityConnectivityStatus(
              terminalConnStatus,
              otherTerminalConnStatus,
            ),
          });
        }

        // Add the waypoints in ascending order
        waypointsToAdd.sort((a: FlightPathWaypoint, b: FlightPathWaypoint) => a.sourceTime - b.sourceTime);
        waypointsToAdd.forEach((waypoint: FlightPathWaypoint) => {
          flightPath = addWaypoint(waypoint, flightPath);
        });
      }

      // update the terminal data in the terminals array
      let updatedTerminals = { ...state.terminals };

      if (isNil(terminal.datapathUuidsOnAircraft)) {
        const datapathUuidsOnAircraft = (updatedTerminals[terminal.datapathUuid] || {}).datapathUuidsOnAircraft;
        updatedTerminals[terminal.datapathUuid] = {
          ...terminal,
          datapathUuidsOnAircraft,
        };
      } else {
        updatedTerminals[terminal.datapathUuid] = {
          ...terminal,
        };
      }

      if (terminal.terminalsOnAircraft && size(terminal.terminalsOnAircraft)) {
        terminal.terminalsOnAircraft.forEach((term: TerminalDetails) => {
          if (term) {
            const datapathUuidsOnAircraft = (updatedTerminals[term.datapathUuid] || {}).datapathUuidsOnAircraft;
            updatedTerminals[term.datapathUuid] = { ...massageTerminalDetails(term), datapathUuidsOnAircraft };
          }
        });
      }
      partialState = {
        selectedTerminalFlightPath: {
          ...state.selectedTerminalFlightPath,
          [action.payload.portal]: flightPath,
        },
        tailDetailsPanelTerminal: terminal,
        terminals: updatedTerminals,
        requestTailDetailsPanelTerminalError: undefined,
        isRequestingTailDetailsPanelTerminal: false,
      };
      break;

    case ActionCreators.ReceivedTailDetailsPanelTerminalError.type:
      partialState = {
        requestTailDetailsPanelTerminalError: action.payload,
        tailDetailsPanelTerminal: undefined,
        isRequestingTailDetailsPanelTerminal: false,
      };
      break;

    case ActionCreators.ClearReceivedTailDetailsPanelTerminalError.type:
      partialState = {
        requestTailDetailsPanelTerminalError: undefined,
      };
      break;

    case ActionCreators.ClearDetailsPanelTerminal.type:
      partialState = {
        tailDetailsPanelTerminal: undefined,
      };
      break;

    case ActionCreators.RequestFlightPath.type:
    case ActionCreators.RequestFlightPathAsync.type:
      partialState = {
        requestingFlightPathError: {
          ...state.requestingFlightPathError,
          [action.payload.portal]: undefined,
        },
        isRequestingFlightPath: true,
      };
      break;

    case ActionCreators.ReceivedFlightPathSuccess.type:
      partialState = {
        selectedTerminalFlightPath: {
          ...state.selectedTerminalFlightPath,
          [action.payload.portal]: action.payload.response.response,
        },
        requestingFlightPathError: {
          ...state.requestingFlightPathError,
          [action.payload.portal]: undefined,
        },
        isRequestingFlightPath: false,
      };
      break;

    case ActionCreators.ClearFlightPath.type:
      partialState = {
        selectedTerminalFlightPath: {
          ...state.selectedTerminalFlightPath,
          [action.payload]: undefined,
        },
      };
      break;

    case ActionCreators.ReceivedFlightPathError.type:
      partialState = {
        requestingFlightPathError: {
          ...state.requestingFlightPathError,
          [action.payload.portal]: action.payload.error,
        },
        selectedTerminalFlightPath: {
          ...state.selectedTerminalFlightPath,
          [action.payload.portal]: undefined,
        },
        isRequestingFlightPath: false,
      };
      break;

    case ActionCreators.ClearReceivedFlightPathError.type:
      partialState = {
        requestingFlightPathError: {
          [PortalsEnum.UNIFIED_PORTAL]: undefined,
          [PortalsEnum.ISAN_PORTAL]: undefined,
        },
      };
      break;

    case ActionCreators.SetFlightPath.type:
      partialState = {
        selectedTerminalFlightPath: {
          ...state.selectedTerminalFlightPath,
          [action.payload.portal]: action.payload.flightPath,
        },
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}

const massageTerminalDisplayName = (
  terminal: Terminal,
  actionPayload: Terminal[],
  terminals: { [key: string]: Terminal } = {},
) => {
  // If the terminal display name is missing, attempt to grab it from the other terminal on aircraft.
  if (isNil(terminal.terminalDisplayName) && size(terminal.datapathUuidsOnAircraft)) {
    let terminalOnAircraft = {} as Terminal;
    if (size(actionPayload)) {
      // If the action payload is provided and not empty, look there for terminal on aircraft.
      terminalOnAircraft =
        actionPayload.find((t: Terminal) => t.datapathUuid === terminal.datapathUuidsOnAircraft[0]) || ({} as Terminal);
    } else if (!isNil(terminals[terminal.datapathUuidsOnAircraft[0]])) {
      // If the terminal hashmap is provided and has the terminal on aircraft, look there instead.
      terminalOnAircraft = terminals[terminal.datapathUuidsOnAircraft[0]];
    }
    terminal.terminalDisplayName = terminalOnAircraft.terminalDisplayName;
  }
  return terminal;
};

const massageTerminalNetworkName = (terminal: Terminal) => {
  // networkOrigin field is not guaranteed to contain exact members of string literal
  // Maintain previous case and space insensitive pattern matching
  if (!isNil(terminal.networkOrigin)) {
    switch (terminal.networkOrigin.toLocaleLowerCase().replace(/ /g, '')) {
      case 'arclight1':
      case 'arclight-1':
        terminal.networkOrigin = 'Arclight-1';
        break;
      case 'viasat':
        terminal.networkOrigin = 'Viasat';
        break;
      default:
        break;
    }
  }
  return terminal;
};

const massageTerminalsInBeam = (terminalsInBeam: Terminal[]) => {
  // If the server were to return null values in the terminalsInBeam array, it will lead to the front end
  // crashing. This step removes any null values from terminalsInBeam
  if (size(terminalsInBeam)) {
    terminalsInBeam = reject(
      terminalsInBeam,
      (terminalInBeam: Terminal) => isNil(terminalInBeam) || isNil(terminalInBeam.datapathUuid),
    );
    // TODO: remove this once the terminal type is returned in the service
    return terminalsInBeam.map((terminal: Terminal) => massageTerminal(terminal));
  } else {
    return terminalsInBeam;
  }
};

const massageTerminal = (terminal: Terminal) => {
  let massagedTerminal: Terminal = {} as Terminal;

  if (terminal) {
    massagedTerminal = { ...terminal };
    massagedTerminal = massageTerminalNetworkName(massagedTerminal);
    // If the server were to return null values in the datapathUuidsOnAircraft array, it will lead to the front end
    // crashing. This step removes any null values from datapathUuidsOnAircraft array
    if (size(massagedTerminal.datapathUuidsOnAircraft)) {
      massagedTerminal.datapathUuidsOnAircraft = reject(
        massagedTerminal.datapathUuidsOnAircraft,
        (datapathUuidOnAircraft: string) => isNil(datapathUuidOnAircraft),
      );
    }
    // If a terminal does not have a platformType, then assign the terminal the airborne type
    if (isNil(massagedTerminal.platformType)) {
      massagedTerminal.platformType = TerminalPlatformsEnum.Airborne;
    }
    // TODO: remove this once the service removes the steerable beam information for offline terminals
    if (!isOnline(massagedTerminal.connectivityStatus)) {
      delete massagedTerminal.steerableBeamPriority;
      delete massagedTerminal.steerableBeamsSummary;
      massagedTerminal.isUsingSteerableBeam = false;
    }

    if (massagedTerminal.headingDeg < MIN_HEADING || massagedTerminal.headingDeg > MAX_HEADING) {
      unset(massagedTerminal, 'headingDeg');
    }
    if (massagedTerminal.latitudeDeg < MIN_LATITUDE || massagedTerminal.latitudeDeg > MAX_LATITUDE) {
      unset(massagedTerminal, 'latitudeDeg');
    }
    if (massagedTerminal.longitudeDeg < MIN_LONGITUDE || massagedTerminal.longitudeDeg > MAX_LONGITUDE) {
      unset(massagedTerminal, 'longitudeDeg');
    }
    if (massagedTerminal.altitudeFeet < ZERO) {
      unset(massagedTerminal, 'altitudeFeet');
    }
    if (massagedTerminal.tthoSeconds < ZERO) {
      unset(massagedTerminal, 'tthoSeconds');
    }
    if (massagedTerminal.flDataRateBps < ZERO) {
      unset(massagedTerminal, 'flDataRateBps');
    }
    if (massagedTerminal.rlDataRateBps < ZERO) {
      unset(massagedTerminal, 'rlDataRateBps');
    }

    if (isNil(massagedTerminal.beamDisplayName)) {
      massagedTerminal.beamDisplayName = massagedTerminal.beamName;
    }
    if (
      !isPlatformTypeInList(terminal.platformType, TerminalPlatformsEnum.Airborne || TerminalPlatformsEnum.Maritime)
    ) {
      unset(massagedTerminal, 'headingDeg');
    }
  }

  return massagedTerminal;
};

export const massageTerminalDetails = (terminal: TerminalDetails) => {
  if (terminal) {
    terminal = massageTerminal(terminal) as TerminalDetails;

    /** Massage the extra properties from TerminalDetails that aren't in a Terminal */

    // TODO: remove this once the terminal type is returned in the service
    if (size(terminal.terminalsOnAircraft)) {
      // For each terminal on the terminal details aircraft, set the platform type to airborne
      terminal.terminalsOnAircraft = reject(
        terminal.terminalsOnAircraft.map(
          (terminalOnAircraft: TerminalDetails) => massageTerminal(terminalOnAircraft) as TerminalDetails,
        ),
        (term: TerminalDetails) => isNil(term) || isNil(term.datapathUuid),
      );
      // For each terminal in beam on other terminal, set the platform type to airborne
      terminal.terminalsOnAircraft.forEach(terminalOnAircraft => {
        terminalOnAircraft.terminalsInBeam = massageTerminalsInBeam(terminalOnAircraft.terminalsInBeam);
      });
    }
    // If the terminal display name is missing, attempt to grab it from the other terminal on aircraft.
    if (size(terminal.terminalsOnAircraft)) {
      if (isNil(terminal.terminalDisplayName)) {
        terminal.terminalDisplayName = (terminal.terminalsOnAircraft[0] || {}).terminalDisplayName;
      } else if (isNil(terminal.terminalsOnAircraft[0].terminalDisplayName)) {
        terminal.terminalsOnAircraft[0].terminalDisplayName = terminal.terminalDisplayName;
      }
    }

    terminal.terminalsInBeam = massageTerminalsInBeam(terminal.terminalsInBeam);

    // If the server were to return null values in the satellitesInFOV array, it will lead to the front end
    // crashing. This step removes any null values from satellitesInFOV array
    if (size(terminal.satellitesInFOV)) {
      terminal.satellitesInFOV = reject(terminal.satellitesInFOV, (satelliteInFov: SatelliteInFov) =>
        isNil(satelliteInFov),
      );
    }

    if (terminal.flDataUseBytes < ZERO) {
      unset(terminal, 'flDataUseBytes');
    }
    if (terminal.rlDataUseBytes < ZERO) {
      unset(terminal, 'rlDataUseBytes');
    }
    if (terminal.packetLossPercentage < ZERO || terminal.packetLossPercentage > MAX_PING_LOSS_PERCENTAGE) {
      unset(terminal, 'packetLossPercentage');
    }
    if (terminal.pingAvgLatency < ZERO) {
      unset(terminal, 'pingAvgLatency');
    }
    if (terminal.velocityKnots < ZERO) {
      unset(terminal, 'velocityKnots');
    }
    if (terminal.elevationDeg < MIN_ELEVATION || terminal.elevationDeg > MAX_ELEVATION) {
      unset(terminal, 'elevationDeg');
    }
    if (terminal.azimuthDeg < MIN_AZIMUTH || terminal.azimuthDeg > MAX_AZIMUTH) {
      unset(terminal, 'azimuthDeg');
    }
    if (terminal.pitchAngleDeg < MIN_PITCH || terminal.pitchAngleDeg > MAX_PITCH) {
      unset(terminal, 'pitchAngleDeg');
    }
    if (terminal.rollAngleDeg < MIN_ROLL || terminal.rollAngleDeg > MAX_ROLL) {
      unset(terminal, 'rollAngleDeg');
    }
    if (
      !isPlatformTypeInList(terminal.platformType, TerminalPlatformsEnum.Airborne || TerminalPlatformsEnum.Maritime)
    ) {
      unset(terminal, 'headingDeg');
    }
  }

  return terminal;
};
